import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle } from "@phosphor-icons/react";
import { api, fmtErr } from "../lib/api";

const FREQUENCIES = [
  { key: "all", label: "Every update", desc: "All newsletters and announcements — the full experience." },
  { key: "monthly", label: "Monthly at most", desc: "No more than one email every 30 days." },
  { key: "paused", label: "Pause for 90 days", desc: "Take a break — emails resume automatically after 3 months." },
];

export default function PreferenceCentrePage() {
  const { contactId } = useParams();
  const [prefs, setPrefs] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [saved, setSaved] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cid, setCid] = useState(contactId);

  useEffect(() => {
    api.publicPrefs(contactId)
      .then(d => { setPrefs(d); setCid(d.contact_id || contactId); setUnsubscribed(d.status === "unsubscribed"); })
      .catch(() => setNotFound(true));
  }, [contactId]);

  const toggleTopic = (t) => {
    const ex = prefs.excluded_topics.includes(t)
      ? prefs.excluded_topics.filter(x => x !== t)
      : [...prefs.excluded_topics, t];
    setPrefs({ ...prefs, excluded_topics: ex });
    setSaved(false);
  };

  const save = async (frequency) => {
    setBusy(true);
    setError("");
    try {
      await api.savePublicPrefs(cid, { frequency: frequency || prefs.frequency, excluded_topics: prefs.excluded_topics });
      if (frequency === "unsubscribe") setUnsubscribed(true);
      else setSaved(true);
    } catch (e) { setError(fmtErr(e)); } finally { setBusy(false); }
  };

  const brand = prefs?.brand_color || "#005191";

  if (notFound) return (
    <Shell brand="#005191">
      <h1 className="font-heading text-xl font-semibold mb-2">Link not recognised</h1>
      <p className="text-sm text-zinc-500">This preferences link doesn't match any contact. If you clicked it from an email, please contact the sender directly.</p>
    </Shell>
  );
  if (!prefs) return <Shell brand="#005191"><p className="text-sm text-zinc-400 text-center py-8">Loading…</p></Shell>;

  if (unsubscribed) return (
    <Shell brand={brand} logo={prefs.logo_url}>
      <div className="text-center py-4" data-testid="prefs-unsubscribed">
        <CheckCircle size={40} weight="fill" className="mx-auto text-emerald-600 mb-3"/>
        <h1 className="font-heading text-xl font-semibold mb-1">You're unsubscribed</h1>
        <p className="text-sm text-zinc-500">{prefs.email} won't receive any more emails from {prefs.company_name}.</p>
      </div>
    </Shell>
  );

  return (
    <Shell brand={brand} logo={prefs.logo_url}>
      <h1 className="font-heading text-xl font-semibold" data-testid="prefs-title">Email preferences</h1>
      <p className="text-sm text-zinc-500 mt-1 mb-5">
        {prefs.first_name ? `Hi ${prefs.first_name} — you're` : "You're"} managing preferences for <strong className="text-zinc-800">{prefs.email}</strong>.
      </p>

      <div className="space-y-2 mb-5" data-testid="prefs-frequency">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">How often would you like to hear from us?</div>
        {FREQUENCIES.map(f => (
          <label key={f.key} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${prefs.frequency === f.key ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
            <input data-testid={`prefs-freq-${f.key}`} type="radio" name="freq" checked={prefs.frequency === f.key} onChange={() => { setPrefs({ ...prefs, frequency: f.key }); setSaved(false); }} className="mt-0.5"/>
            <span>
              <span className="block text-sm font-medium text-zinc-900">{f.label}</span>
              <span className="block text-xs text-zinc-500">{f.desc}</span>
            </span>
          </label>
        ))}
      </div>

      {prefs.topics.length > 0 && (
        <div className="mb-5" data-testid="prefs-topics">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">Topics you want to receive</div>
          <div className="space-y-1.5">
            {prefs.topics.map(t => (
              <label key={t} className="flex items-center gap-2.5 text-sm cursor-pointer">
                <input data-testid={`prefs-topic-${t}`} type="checkbox" checked={!prefs.excluded_topics.includes(t)} onChange={() => toggleTopic(t)}/>
                {t}
              </label>
            ))}
          </div>
        </div>
      )}

      {error && <p className="text-xs text-rose-600 mb-3">{error}</p>}
      {saved && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mb-3" data-testid="prefs-saved">Preferences saved — thank you!</p>}

      <button data-testid="prefs-save" disabled={busy} onClick={() => save()} className="w-full text-sm font-semibold text-white rounded-md py-2.5 disabled:opacity-50" style={{ backgroundColor: brand }}>
        {busy ? "Saving…" : "Save my preferences"}
      </button>

      <div className="text-center mt-4">
        <button data-testid="prefs-unsubscribe" disabled={busy} onClick={() => { if (window.confirm("Unsubscribe from ALL emails? You can only be re-added by contacting us.")) save("unsubscribe"); }} className="text-xs text-zinc-400 underline hover:text-zinc-700">
          Unsubscribe from everything instead
        </button>
      </div>
    </Shell>
  );
}

const Shell = ({ brand, logo, children }) => (
  <div className="min-h-screen bg-zinc-100 py-10 px-4" data-testid="preference-centre">
    <div className="max-w-md mx-auto">
      <div className="h-1.5 rounded-t-xl" style={{ backgroundColor: brand }}/>
      <div className="bg-white rounded-b-xl border border-zinc-200 border-t-0 shadow-sm p-6">
        {logo && <img src={logo} alt="logo" className="h-9 object-contain mb-4"/>}
        {children}
      </div>
      <p className="text-center text-[11px] text-zinc-400 mt-4">Powered by MediHub</p>
    </div>
  </div>
);
